import React, { createContext, useCallback, useContext, useEffect, useMemo, useState } from 'react'
import { supabase } from '../supabase'
import { can as canRole } from '../lib/permissions'

const AuthContext = createContext(null)

const PROFILE_COLS = 'id, user_id, clinic_id, role, full_name, qualification, registration_no, registration_council'

export function AuthProvider({ children }) {
  const [session, setSession] = useState(undefined) // undefined = still loading
  const [userProfile, setUserProfile] = useState(null)
  const [clinic, setClinic] = useState(null)
  const [doctors, setDoctors] = useState([])

  // Clinic row + registered doctors are fetched once per login and cached here
  const loadClinicData = useCallback(async (clinicId) => {
    if (!clinicId) { setClinic(null); setDoctors([]); return }
    const [clinicRes, docRes] = await Promise.all([
      supabase.from('clinics').select('id, name, tagline, address, phone, email').eq('id', clinicId).maybeSingle(),
      supabase.from('user_profiles').select(PROFILE_COLS)
        .eq('clinic_id', clinicId).not('registration_no', 'is', null).order('full_name'),
    ])
    if (clinicRes.error) console.warn('clinic fetch failed:', clinicRes.error.message)
    if (docRes.error)    console.warn('doctor list fetch failed:', docRes.error.message)
    setClinic(clinicRes.data ?? null)
    // Profiles may exist before a login is linked (user_id null) — they are still valid doctors
    setDoctors((docRes.data || []).filter(d => d && d.full_name))
  }, [])

  const fetchUserProfile = useCallback(async (userId) => {
    if (!userId) { setUserProfile(null); setClinic(null); setDoctors([]); return }
    try {
      const { data, error } = await supabase
        .from('user_profiles')
        .select(PROFILE_COLS)
        .eq('user_id', userId)
        .maybeSingle()
      if (error) {
        console.warn('user_profiles fetch skipped:', error.message)
        setUserProfile(null)
        return
      }
      setUserProfile(data ?? null)
      await loadClinicData(data?.clinic_id)
    } catch (err) {
      console.warn('user_profiles fetch error:', err.message)
      setUserProfile(null)
    }
  }, [loadClinicData])

  useEffect(() => {
    supabase.auth.getSession().then(({ data: { session } }) => {
      setSession(session)
      fetchUserProfile(session?.user?.id ?? null)
    })

    const { data: { subscription } } = supabase.auth.onAuthStateChange((event, session) => {
      setSession(session)
      // Token refreshes don't change who is logged in — skip the refetch
      if (event === 'TOKEN_REFRESHED') return
      fetchUserProfile(session?.user?.id ?? null)
    })

    return () => subscription.unsubscribe()
  }, [fetchUserProfile])

  async function login(email, password) {
    const { data, error } = await supabase.auth.signInWithPassword({ email, password })
    if (error) throw error
    return data
  }

  async function logout() {
    const { error } = await supabase.auth.signOut()
    if (error) throw error
  }

  async function resetPassword(email) {
    const { error } = await supabase.auth.resetPasswordForEmail(email, {
      redirectTo: process.env.REACT_APP_PASSWORD_RESET_REDIRECT_URL || window.location.origin,
    })
    if (error) throw error
  }

  const refreshClinic = useCallback(() => loadClinicData(userProfile?.clinic_id), [loadClinicData, userProfile?.clinic_id])

  const value = useMemo(() => {
    const role = userProfile?.role || null
    return {
      session, userProfile, clinic, doctors, role,
      isAdmin: role === 'admin',
      can: action => canRole(role, action),
      refreshClinic, login, logout, resetPassword,
    }
  }, [session, userProfile, clinic, doctors, refreshClinic]) // eslint-disable-line react-hooks/exhaustive-deps

  return (
    <AuthContext.Provider value={value}>
      {children}
    </AuthContext.Provider>
  )
}

export function useAuth() {
  const ctx = useContext(AuthContext)
  if (!ctx) throw new Error('useAuth must be used inside <AuthProvider>')
  return ctx
}
